import { Component, signal, inject, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { RoleService } from '../auth/role-service';
import { Role } from 'shared-lib';

interface HomeLink {
  id: string;
  title: string;
  description: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-home',
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, RouterLink, TranslatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  private readonly keycloak = inject(Keycloak);
  private readonly roleService = inject(RoleService);

  isAdmin = signal(false);
  isOperatorAdmin = signal(false);
  homeLinks = signal<HomeLink[]>([]);

  protected readonly environment = environment;

  constructor() {
    if (!this.keycloak.authenticated) {
      this.keycloak.login();
    }
  }

  async ngOnInit(): Promise<void> {
    await this.initializeLinks();
  }

  private async initializeLinks(): Promise<void> {
    const links: HomeLink[] = [];

    const isAdmin = await this.roleService.checkAccess(Role.Administrator);
    const isOperatorAdmin = await this.roleService.checkAccess(Role.OperatorAdmin);
    const isOperatorMember = await this.roleService.checkAccess(Role.OperatorMember);

    this.isAdmin.set(isAdmin);
    this.isOperatorAdmin.set(isOperatorAdmin);

    // Administrator-only: Customer Management
    if (isAdmin) {
      links.push({
        id: 'customer-management',
        title: 'nav.customerManagement',
        description: 'home.desc.customerManagement',
        icon: 'business',
        route: '/customer-management'
      });
    }

    // Parking space is available to operator admins and (restricted) operator members —
    // members get a stripped-down view (name + gate hotline only).
    if (isOperatorAdmin || isOperatorMember) {
      links.push({
        id: 'parking-space',
        title: 'nav.parkingSpace',
        description: 'home.desc.parkingSpace',
        icon: 'dashboard',
        route: '/parking-space'
      });
    }

    // Operator Admin features
    if (isOperatorAdmin) {
      // links.push({
      //   id: 'space-tenants',
      //   title: 'Space Tenants',
      //   description: 'Manage tenants assigned to parking spaces',
      //   icon: 'groups',
      //   route: '/space-tenants'
      // });

      links.push({
        id: 'parking-rights',
        title: 'nav.parkingRights',
        description: 'home.desc.parkingRights',
        icon: 'verified_user',
        route: '/parking-rights'
      });

      // User Management - Admin only
      if (isAdmin) {
        links.push({
          id: 'user-management',
          title: 'nav.userManagement',
          description: 'home.desc.userManagement',
          icon: 'manage_accounts',
          route: '/user-management'
        });
      }
    }
    const hasCameraExport = await this.roleService.checkAccess(Role.CameraDataExport);
    if (hasCameraExport) {
      links.push({
        id: 'camera-export',
        title: 'nav.detectionExport',
        description: 'home.desc.cameraExport',
        icon: 'file_download',
        route: '/camera-export'
      });
    }

    // Authenticated user features
    if (this.roleService.isAuthenticated() && !hasCameraExport) {
      links.push({
        id: 'my-account',
        title: 'nav.myAccount',
        description: 'home.desc.myAccount',
        icon: 'account_circle',
        route: '/my-account'
      });
    }

    this.homeLinks.set(links);
  }
}
